/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { AuthConfigurationV3 } from "@tivocorp/data-domain-lib";

import type { UnityRootState } from "../../UnityAppStore";
import { AUTH_TYPE_KIND_INTERACTIVE } from "../signIn.types";
import type { SignInFocusedField } from "../signIn.types";
import { classifyAuthType } from "../common";

export const selectIsInitializing = (state: UnityRootState): boolean =>
  state.signIn.isInitializing;

export const selectIsInitialized = (state: UnityRootState): boolean =>
  state.signIn.isInitialized;

export const selectInitError = (state: UnityRootState): string =>
  state.signIn.initError;

export const selectInitErrorCode = (state: UnityRootState): string =>
  state.signIn.initErrorCode;

export const selectInitErrorServiceCode = (state: UnityRootState): string =>
  state.signIn.initErrorServiceCode;

export const selectAuthConfigurations = (
  state: UnityRootState
): AuthConfigurationV3[] => state.signIn.authConfigurations;

/**
 * The auth configurations that get a tab. Silent (tivoIpAutoLoginIdp) and
 * unsupported types never appear in the switcher, so they must not be
 * counted when deciding whether to show it.
 */
function getTabbedAuthConfigurations(
  state: UnityRootState
): AuthConfigurationV3[] {
  return state.signIn.authConfigurations.filter(
    (c: AuthConfigurationV3): boolean =>
      classifyAuthType(c.authenticationType) === AUTH_TYPE_KIND_INTERACTIVE
  );
}

export const selectTabbedAuthConfigurations = (
  state: UnityRootState
): AuthConfigurationV3[] => getTabbedAuthConfigurations(state);

export const selectShowTabs = (state: UnityRootState): boolean =>
  getTabbedAuthConfigurations(state).length > 1;

export const selectActiveTab = (state: UnityRootState): string =>
  state.signIn.activeTab;

export const selectUserId = (state: UnityRootState): string =>
  state.signIn.userId;

export const selectPassword = (state: UnityRootState): string =>
  state.signIn.password;

export const selectIsPasswordVisible = (state: UnityRootState): boolean =>
  state.signIn.isPasswordVisible;

export const selectAuthCode = (state: UnityRootState): string =>
  state.signIn.authCode;

export const selectQrCodeUrl = (state: UnityRootState): string =>
  state.signIn.qrCodeUrl;

export const selectQrCodeBitmap = (state: UnityRootState): string =>
  state.signIn.qrCodeBitmap;

export const selectFocusedField = (state: UnityRootState): SignInFocusedField =>
  state.signIn.focusedField;

export const selectIsSigningIn = (state: UnityRootState): boolean =>
  state.signIn.isSigningIn;

export const selectSignInError = (state: UnityRootState): string =>
  state.signIn.signInError;

export const selectSignInErrorCode = (state: UnityRootState): string =>
  state.signIn.signInErrorCode;

/**
 * The service's own code for the surfaced error — what the alert shows in
 * parentheses. Empty when the failure carried no response envelope.
 */
export const selectSignInErrorServiceCode = (state: UnityRootState): string =>
  state.signIn.signInErrorServiceCode !== undefined
    ? state.signIn.signInErrorServiceCode
    : "";

export const selectMobileDialogDismissed = (state: UnityRootState): boolean =>
  state.signIn.mobileDialogDismissed !== undefined
    ? state.signIn.mobileDialogDismissed
    : false;
